import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Modal from '../../../../../../components/uiComponent/modal';
import styled from 'styled-components';
import { Spin, Progress, notification, Tag, Tooltip, Empty, Icon } from 'antd';
import { FiTrash2, FiPrinter } from 'react-icons/fi';
import { GetPackScanInfo, GetLabels, DeletePackScanInfo } from '../../../../../../slices/post-shipment';

const StyledWrapper = styled.div`
  background: #F4F7F9;
  font-family: 'Inter', -apple-system, sans-serif;
  height: 82vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: #1F1F1F;

  .modal-header {
    background: #FFFFFF;
    padding: 12px 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #E8ECF1;
    z-index: 10;

    .header-left {
      display: flex;
      align-items: center;
      gap: 40px;
      flex: 1;
    }

    .title-group {
      display: flex;
      align-items: center;
      gap: 12px;
      .icon-box {
        background: #1565D8;
        color: #FFFFFF;
        width: 36px;
        height: 36px;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
      }
      .text-wrap {
        h1 { margin: 0; font-size: 18px; font-weight: 500; color: #1B2559; }
        p { margin: 0; font-size: 12px; color: #64748B; font-weight: 400; span { color: #1565D8; font-weight: 600; } }
      }
    }

    .elegant-progress {
      width: 280px;
      .progress-info {
        display: flex;
        justify-content: space-between;
        font-size: 11px;
        font-weight: 500;
        margin-bottom: 4px;
        color: #535E63;
        .percent { color: #1565D8; font-weight: 600; }
      }
    }
    
    .header-actions {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .btn-action {
      background: #1565D8;
      color: #FFFFFF;
      border: none;
      padding: 8px 16px;
      border-radius: 6px;
      font-weight: 500;
      font-size: 12px;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: all 0.2s;
      &:hover:not(:disabled) { background: #1255B8; filter: brightness(0.92); }
      &:disabled { opacity: 0.6; cursor: not-allowed; }
    }

    .close-x {
      background: none;
      border: none;
      font-size: 24px;
      color: #BEC7CB;
      cursor: pointer;
      line-height: 1;
      &:hover { color: #1565D8; }
    }
  }

  .content-grid {
    flex: 1;
    padding: 16px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .main-tables {
    display: grid;
    grid-template-columns: 1.5fr 1fr;
    gap: 16px;
    flex: 1;
    min-height: 0;
  }

  .glass-panel {
    background: #FFFFFF;
    border: 1px solid #E8ECF1;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);

    .panel-head {
      padding: 14px 18px;
      border-bottom: 1px solid #EEF2F4;
      display: flex;
      justify-content: space-between;
      align-items: center;
      h3 { margin: 0; font-size: 14px; font-weight: 500; color: #1B2559; }
    }

    .search-bar {
      display: flex;
      align-items: center;
      background: #FFFFFF;
      border: 1px solid #E8ECF1;
      border-radius: 6px;
      padding: 4px 10px;
      gap: 8px;
      input {
        border: none;
        background: transparent;
        outline: none;
        font-size: 12px;
        width: 150px;
        color: #1F1F1F;
        font-weight: 400;
        &::placeholder { color: #A3ADB3; }
      }
      .anticon { color: #808183; }
    }

    .table-wrapper {
      flex: 1;
      overflow-y: auto;
      table {
        width: 100%;
        border-collapse: collapse;
        th {
          background: #F8FAFB;
          padding: 10px 18px;
          text-align: left;
          font-size: 11px;
          text-transform: uppercase;
          color: #808183;
          font-weight: 500;
          letter-spacing: 0.04em;
          border-bottom: 1px solid #E8ECF1;
          position: sticky;
          top: 0;
          z-index: 1;
        }
        td {
          padding: 12px 18px;
          border-bottom: 1px solid #F0F2F5;
          font-size: 13px;
          color: #535E63;
          font-weight: 400;
        }
        tr:hover td { background: #F8FBFF; }
        .bold { font-weight: 500; color: #1B2559; }
        .dim { color: #64748B; font-size: 12px; font-weight: 400; }
        .qty-badge {
          background: #F0F4FF;
          color: #1565D8;
          border: 1px solid #D6E4FF;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
        }
        .scanned-row td { background: #F0FFF5 !important; }
      }
    }
  }

  .row-actions {
    display: flex;
    gap: 8px;
    justify-content: center;
  }

  .action-btn {
    border: 1px solid #DDE5EA;
    background: #FFFFFF;
    width: 28px;
    height: 28px;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #808183;
    transition: all 0.2s;
    &:hover { border-color: #1565D8; color: #1565D8; background: #F5FBFF; }
    &.delete-btn:hover { border-color: #D64545; color: #D64545; background: #FFF5F5; }
  }

  .scanner-footer {
    background: #FFFFFF;
    padding: 16px;
    border-top: 1px solid #DDE5EA;
    display: flex;
    justify-content: center;

    .scanner-container {
      width: 100%;
      max-width: 500px;
      .input-focus-wrapper {
        display: flex;
        align-items: center;
        border: 2px solid #1565D8;
        border-radius: 4px;
        padding: 0 12px;
        background: #FFFFFF;
        box-shadow: 0 4px 12px rgba(21, 101, 216, 0.1);
        &.loading { background: #F8FAFB; opacity: 0.7; }
        .scan-icon-main { font-size: 20px; color: #1565D8; }
        input {
          flex: 1;
          padding: 12px;
          border: none;
          outline: none;
          font-size: 14px;
          font-weight: 600;
          color: #1F1F1F;
          &::placeholder { color: #A3ADB3; font-weight: 400; }
        }
      }
      .hint { text-align: center; font-size: 11px; color: #808183; margin-top: 8px; }
    }
  }
`;

const PackScanModal = ({ open, onClose, workOrderId, forcedPageType }) => {
  const dispatch = useDispatch();
  const { transportationDetails = [], shipmentDetails } = useSelector((state) => state.postShipment || {});
  const inputRef = useRef(null);

  const isNonPartnered = (shipmentId) => {
    const detail = transportationDetails.find(d => d.shipmentId === shipmentId);
    if (detail) return detail.carrierType === 'non-partnered';
    const shipment = shipmentDetails?.shipments?.find(s => s.amazon_shipment_id === shipmentId);
    return shipment?.carrier_type === 'non-partnered';
  };

  const getPageType = (shipmentId, type) => {
    if (forcedPageType) return forcedPageType;
    if (type === 'LTL' || isNonPartnered(shipmentId)) return 'PackageLabel_Thermal_NonPCP';
    return 'PackageLabel_Thermal';
  };

  const [scanCode, setScanCode] = useState('');
  const [skuFilter, setSkuFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printingBox, setPrintingBox] = useState(null);
  const [activeShipmentId, setActiveShipmentId] = useState(null);
  const [scannedCodes, setScannedCodes] = useState([]);
  const [totalBoxes, setTotalBoxes] = useState(0);
  const [shipments, setShipments] = useState([]);
  const [allBoxes, setAllBoxes] = useState([]);

  useEffect(() => {
    if (open && workOrderId) {
      setLoading(true);
      dispatch(GetPackScanInfo({ workOrderId })).then(({ payload }) => {
        if (payload?.success) {
          setShipments(payload.shipments || []);
          setAllBoxes(payload.allBoxes || []);
          setTotalBoxes(payload.totalBoxes || 0);

          const alreadyScanned = (payload.allBoxes || [])
            .filter(b => b.isScanned);
          
          if (alreadyScanned.length > 0) {
            setScannedCodes(alreadyScanned.map(b => b.boxCode));
          }
        }
        setLoading(false);
      });
    }
    if (!open) {
      setScannedCodes([]);
      setScanCode('');
      setSkuFilter('');
      setShipments([]);
      setAllBoxes([]);
      setActiveShipmentId(null);
    }
  }, [dispatch, open, workOrderId]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  const handleScan = () => {
    if (!scanCode.trim()) return;
    const code = scanCode.trim().toUpperCase();
    
    setLoading(true);
    dispatch(GetPackScanInfo({ workOrderId, boxCode: code })).then(({ payload }) => {
      if (payload?.success) {
        if (payload.matchedBox) {
          setActiveShipmentId(payload.matchedBox.shipmentId);
          setShipments(payload.shipments || []);
          setAllBoxes(payload.allBoxes || []);
          setTotalBoxes(payload.totalBoxes || 0);
          
          const updatedScanned = (payload.allBoxes || [])
            .filter(b => b.isScanned)
            .map(b => b.boxCode);
          setScannedCodes(updatedScanned);
          
          handlePrint(payload.matchedBox, payload.allBoxes);
        } else {
          notification.error({ message: 'Invalid Box Code', description: 'This box code was not found.' });
        }
      }
      setLoading(false);
      setScanCode('');
      inputRef.current?.focus();
    });
  };

  const handleGlobalPrint = async () => {
    if (!allBoxes.length) {
      setLoading(true);
      const { payload } = await dispatch(GetPackScanInfo({ workOrderId }));
      if (payload?.success) {
        const boxes = payload.allBoxes || [];
        const scannedBoxes = boxes.filter(b => b.isScanned);
        if (!scannedBoxes.length) {
          notification.info({ message: 'No Scanned Boxes', description: 'Please scan boxes before printing labels.' });
          setLoading(false);
          return;
        }
        await triggerGlobalPrint(scannedBoxes);
      }
      setLoading(false);
    } else {
      const scannedBoxes = allBoxes.filter(b => b.isScanned);
      if (!scannedBoxes.length) {
        notification.info({ message: 'No Scanned Boxes', description: 'Please scan boxes before printing labels.' });
        return;
      }
      await triggerGlobalPrint(scannedBoxes);
    }
  };

  const triggerGlobalPrint = async (boxes) => {
    setPrinting(true);
    try {
      const boxesByShipment = {};
      boxes.forEach(box => {
        if (!boxesByShipment[box.shipmentId]) boxesByShipment[box.shipmentId] = [];
        boxesByShipment[box.shipmentId].push(box);
      });

      for (const sid of Object.keys(boxesByShipment)) {
        const shipmentBoxes = boxesByShipment[sid];
        const cartonIdList = shipmentBoxes.map(b => b.amazonBoxId).filter(Boolean);
        
        if (cartonIdList.length > 0) {
          const { type: boxType } = shipmentBoxes[0] || {};
          await dispatch(GetLabels({
            shipmentId: sid,
            PageType: getPageType(sid, boxType),
            LabelType: 'UNIQUE',
            cartonIdList
          }));
        }
      }
      notification.success({ message: 'Labels Generated', description: 'Amazon labels have been requested.' });
    } catch (err) {
      console.error('Global print error:', err);
      notification.error({ message: 'Print Error', description: 'Failed to generate Amazon labels.' });
    } finally {
      setPrinting(false);
    }
  };

  const handleUnscan = (code) => {
    dispatch(DeletePackScanInfo({ workOrderId, boxCode: code })).then(({ payload }) => {
      if (payload?.success) {
        notification.success({ message: 'Box Unscanned', description: 'Box scan progress has been deleted.' });
        dispatch(GetPackScanInfo({ workOrderId })).then(({ payload: refreshPayload }) => {
          if (refreshPayload?.success) {
            setShipments(refreshPayload.shipments || []);
            setAllBoxes(refreshPayload.allBoxes || []);
            setTotalBoxes(refreshPayload.totalBoxes || 0);
            const updatedScanned = (refreshPayload.allBoxes || [])
              .filter(b => b.isScanned)
              .map(b => b.boxCode);
            setScannedCodes(updatedScanned);
          }
        });
      }
    });
  };

  const handlePrint = (boxItem, boxesOverride) => {
    if (!boxItem?.shipmentId || !boxItem?.ship) {
      notification.error({ message: 'Print Error', description: 'Missing shipment details.' });
      return;
    }
    const boxesToUse = boxesOverride || allBoxes;
    setPrintingBox(boxItem.boxId);
    try {
      const { confirmationId, shipmentId, shipmentName, items = [], ship, type, amazonBoxId } = boxItem;
      const shipmentBoxes = boxesToUse.filter(b => b.shipmentId === boxItem.shipmentId);
      if (amazonBoxId) {
        dispatch(GetLabels({
          shipmentId: shipmentId,
          PageType: getPageType(shipmentId, type),
          LabelType: 'UNIQUE',
          cartonIdList: [amazonBoxId]
        })).then(({ payload }) => {
          if (!payload?.success) {
            notification.warning({
              message: 'Carrier Label Missing',
              description: 'Amazon carrier labels may not be ready yet. Please check your transportation status.'
            });
          }
        });
      }

    } catch (err) {
      console.error('Print error:', err);
      notification.error({ message: 'Print Error', description: 'Failed to generate PDF.' });
    }
    setPrintingBox(null);
  };

  const displayShipments = shipments.filter(s => s.scannedLabels > 0);
  const displayBoxes = allBoxes.filter(b => b.isScanned);
  const filteredBoxes = displayBoxes
    .filter(box => 
      !skuFilter || 
      box.shipmentId?.toLowerCase().includes(skuFilter.toLowerCase()) || 
      box.boxCode?.toLowerCase().includes(skuFilter.toLowerCase()) ||
      box.skus?.toLowerCase().includes(skuFilter.toLowerCase())
    );

  const totalScannableBoxes = allBoxes.filter(b => b.type === 'SP' || b.type === 'LTL').length;
  const progressPercent = totalScannableBoxes > 0 ? Math.round((scannedCodes.length / totalScannableBoxes) * 100) : 0;

  return (
    <Modal open={open} onHide={onClose} size="xl">
      <StyledWrapper>
        <header className="modal-header">
          <div className="header-left">
            <div className="title-group">
              <div className="icon-box"><Icon type="scan" /></div>
              <div className="text-wrap">
                <h1>Pack & Scan</h1>
            
              </div>
            </div>
            
            <div className="elegant-progress">
              <div className="progress-info">
              
                <span className="percent">{progressPercent}%</span>
              </div>
              <Progress 
                percent={progressPercent} 
                strokeColor={
                  progressPercent === 100 ? '#52C41A' : 
                  progressPercent >= 50 ? '#FA8C16' : '#FAAD14'
                }
                strokeWidth={6}
                showInfo={false}
              />
            </div>
          </div>

          <div className="header-actions">
            {/* <button className="btn-action" onClick={handleGlobalPrint} disabled={loading || printing}>
              {printing ? <Spin size="small" /> : <><Icon type="printer" /> Print All Labels</>}
            </button> */}
            <button className="close-x" onClick={onClose}>&times;</button>
          </div>
        </header>

        <main className="content-grid">
          <div className="main-tables">
            <div className="glass-panel">
              <div className="panel-head">
                <h3>Shipment Summary</h3>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                    
                      <th>Shipment ID</th>
                      <th>FC</th>
                      <th>Boxes</th>
                      <th>Box Scanned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayShipments.map((s, i) => {
                       const shipScanned = s.scannedLabels || 0;
                       const isComplete = shipScanned >= s.boxes && s.boxes > 0;
                       return (
                        <tr key={i} className={isComplete ? 'complete-row' : ''}>
                         
                          <td className="bold">{s.confirmationId || s.shipmentId}</td>
                          <td>{s.fc}</td>
                          <td>{s.boxes}</td>
                          <td className="bold" >
                            {shipScanned}
                          </td>
                        </tr>
                       );
                    })}
                  </tbody>
                </table>
                {displayShipments.length === 0 && (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No shipments scanned" />
                )}
              </div>
            </div>

            <div className="glass-panel">
              <div className="panel-head">
                <h3>Box Contents</h3>
                <div className="search-bar">
                  <Icon type="search" />
                  <input placeholder="Search by SKU, Box #, Shipment..." value={skuFilter} onChange={e => setSkuFilter(e.target.value)} />
                </div>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Box #</th>
                      <th>Shipment ID</th>
                      <th>Qty</th>
                      <th className="centered">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBoxes.map((box, i) => {
                      const matchedShipment = shipments.find(s => s.shipmentId === box.shipmentId);
                      const displayShipmentId = matchedShipment ? (matchedShipment.confirmationId || matchedShipment.shipmentId) : box.shipmentId;
                      
                      const shipmentBoxes = allBoxes.filter(b => b.shipmentId === box.shipmentId);
                      const relativeBoxNum = shipmentBoxes.findIndex(b => b.boxId === box.boxId) + 1 || box.boxNumber;
                      
                      return (
                        <tr key={i} className={scannedCodes.includes(box.boxCode) ? 'scanned-row' : ''}>
                          <td className="bold">Box {relativeBoxNum}</td>
                          <td className="dim">{displayShipmentId}</td>
                          <td><span className="qty-badge">{box.qty} Units</span></td>
                          <td>
                            <div className="row-actions">
                              <Tooltip title="Print Label">
                                <button 
                                  className="action-btn" 
                                  disabled={printingBox === box.boxId}
                                  onClick={async () => {
                                    setPrintingBox(box.boxId);
                                    const { payload } = await dispatch(GetPackScanInfo({ workOrderId, boxCode: box.boxCode }));
                                    if (payload?.success && payload.matchedBox) {
                                      handlePrint(payload.matchedBox, payload.allBoxes);
                                    } else {
                                      notification.error({ message: 'Print Error', description: 'Box data not found.' });
                                    }
                                    setPrintingBox(null);
                                  }}
                                  style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer' }}
                                >
                                  {printingBox === box.boxId ? <Spin size="small" /> : <FiPrinter size={18} />}
                                </button>
                              </Tooltip>
                              <Tooltip title="Delete/Unscan">
                                <button 
                                  className="action-btn" 
                                  onClick={() => handleUnscan(box.boxCode)}
                                  style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', color: '#D95040' }}
                                >
                                  <FiTrash2 size={18} />
                                </button>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filteredBoxes.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No boxes scanned" />}
              </div>
            </div>
          </div>
        </main>

        <footer className="scanner-footer">
          <div className="scanner-container">
            <div className={`input-focus-wrapper ${loading ? 'loading' : ''}`}>
              <Icon type="scan" className="scan-icon-main" />
              <input
                ref={inputRef}
                value={scanCode}
                onChange={e => setScanCode(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleScan()}
                placeholder="Scan box barcode..."
                autoFocus
              />
              {loading && <Spin className="loader" size="small" />}
            </div>
            <p className="hint">Waiting for barcode scan...</p>
          </div>
        </footer>
      </StyledWrapper>
    </Modal>
  );
};

export default PackScanModal;
